import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, GripVertical, Pencil, RotateCcw, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES_KEYS = [
  { value: 'text', key: 'shortText' },
  { value: 'textarea', key: 'longText' },
  { value: 'select', key: 'selector' },
  { value: 'radio', key: 'options' },
  { value: 'slider', key: 'scale' },
  { value: 'photos', key: 'photos' },
];

const FieldEditor = ({ field, onChange, onDelete }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const FIELD_TYPES = FIELD_TYPES_KEYS.map(ft => ({ value: ft.value, label: t(`formEditor.${ft.key}`) }));

  const updateField = (key, value) => {
    onChange({ ...field, [key]: value });
  };

  const addOption = () => {
    const opts = field.options || [];
    updateField('options', [...opts, { value: `opt_${opts.length + 1}`, label: '' }]);
  };

  const updateOption = (idx, key, val) => {
    const opts = [...(field.options || [])];
    opts[idx] = { ...opts[idx], [key]: val };
    updateField('options', opts);
  };

  const removeOption = (idx) => {
    const opts = [...(field.options || [])];
    opts.splice(idx, 1);
    updateField('options', opts);
  };

  return (
    <div className="border rounded-lg p-3 bg-card" data-testid={`field-editor-${field.id}`}>
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate">{field.label || t('common.noName')}</span>
          <span className="text-xs text-muted-foreground ml-2">
            ({FIELD_TYPES.find(ft => ft.value === field.type)?.label || field.type})
          </span>
          {field.required && <span className="text-xs text-red-500 ml-1">*</span>}
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 className="w-3.5 h-3.5 text-red-400" />
        </Button>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 pl-6">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('formEditor.label')}</Label>
              <Input value={field.label} onChange={(e) => updateField('label', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">{t('formEditor.type')}</Label>
              <Select value={field.type} onValueChange={(v) => updateField('type', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Placeholder</Label>
            <Input value={field.placeholder || ''} onChange={(e) => updateField('placeholder', e.target.value)} className="h-8 text-sm" placeholder="Texto de ayuda..." />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={field.required} onCheckedChange={(v) => updateField('required', v)} />
            <Label className="text-xs">{t('formEditor.required')}</Label>
          </div>

          {(field.type === 'select' || field.type === 'radio') && (
            <div>
              <Label className="text-xs mb-1 block">{t('formEditor.options')}</Label>
              <div className="space-y-1">
                {(field.options || []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input value={opt.label} onChange={(e) => updateOption(idx, 'label', e.target.value)} className="h-7 text-xs flex-1" placeholder={t('formEditor.optionLabel')} />
                    <Input value={opt.value} onChange={(e) => updateOption(idx, 'value', e.target.value)} className="h-7 text-xs w-24" placeholder={t('formEditor.optionValue')} />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeOption(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addOption}>
                  <Plus className="w-3 h-3" /> {t('formEditor.addOption')}
                </Button>
              </div>
            </div>
          )}

          {field.type === 'slider' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('formEditor.min')}</Label>
                <Input type="number" value={field.min_val || 1} onChange={(e) => updateField('min_val', parseInt(e.target.value))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">{t('formEditor.max')}</Label>
                <Input type="number" value={field.max_val || 5} onChange={(e) => updateField('max_val', parseInt(e.target.value))} className="h-8 text-sm" />
              </div>
            </div>
          )}

          {field.type === 'photos' && (
            <div className="p-2 bg-muted/30 rounded-md text-xs text-muted-foreground">
              Max {field.max_photos || 2} fotos por anecdota · JPG, PNG, WebP · 5MB max
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionEditor = ({ section, onChange, onDelete }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);

  const updateSection = (key, value) => {
    onChange({ ...section, [key]: value });
  };

  const updateField = (idx, field) => {
    const fields = [...section.fields];
    fields[idx] = field;
    updateSection('fields', fields);
  };

  const addField = () => {
    const newId = `field_${Date.now()}`;
    updateSection('fields', [...section.fields, {
      id: newId, type: 'text', label: t('formEditor.newField'), placeholder: '', required: false
    }]);
  };

  const removeField = (idx) => {
    const fields = [...section.fields];
    fields.splice(idx, 1);
    updateSection('fields', fields);
  };

  return (
    <Card className="border-l-4 border-l-primary/30" data-testid={`section-editor-${section.id}`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {editingTitle ? (
              <Input
                autoFocus
                value={section.title}
                onChange={(e) => updateSection('title', e.target.value)}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingTitle(false); }}
                className="h-7 text-sm font-semibold max-w-xs"
                onClick={(e) => e.stopPropagation()}
                data-testid={`section-title-input-${section.id}`}
              />
            ) : (
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-sm font-semibold">{section.title}</CardTitle>
                <button type="button" onClick={(e) => { e.stopPropagation(); setEditingTitle(true); }} className="p-0.5 rounded hover:bg-muted transition-colors">
                  <Pencil className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            )}
            <span className="text-xs text-muted-foreground flex-shrink-0">({section.fields.length} {t('formEditor.fields')})</span>
            {section.repeatable && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">
                {t('formEditor.repeatableTag')} x{section.max_repeat}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
            <button type="button" className="p-1" onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0 px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs">{t('formEditor.description')}</Label>
              <Input value={section.description || ''} onChange={(e) => updateSection('description', e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={section.repeatable} onCheckedChange={(v) => updateSection('repeatable', v)} />
              <Label className="text-xs">{t('formEditor.repeatable')}</Label>
            </div>
            {section.repeatable && (
              <div className="flex items-center gap-2">
                <Label className="text-xs">{t('formEditor.maxRepeat')}</Label>
                <Input type="number" value={section.max_repeat} onChange={(e) => updateSection('max_repeat', parseInt(e.target.value) || 1)} className="h-7 w-16 text-sm" min="1" max="10" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            {section.fields.map((field, idx) => (
              <FieldEditor key={field.id} field={field} onChange={(f) => updateField(idx, f)} onDelete={() => removeField(idx)} />
            ))}
          </div>
          <Button variant="outline" size="sm" className="w-full gap-1 text-xs" onClick={addField}>
            <Plus className="w-3 h-3" /> {t('formEditor.addField')}
          </Button>
        </CardContent>
      )}
    </Card>
  );
};

const FormTemplateEditor = ({ bookId, onClose }) => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    fetchTemplate();
  }, [bookId]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/books/${bookId}/form-template`, { withCredentials: true });
      setTemplate(res.data.form_template);
    } catch (err) {
      toast.error(t('formEditor.formLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/books/${bookId}/form-template`, { sections: template.sections }, { withCredentials: true });
      toast.success(t('formEditor.formSaved'));
    } catch (err) {
      toast.error(t('formEditor.formSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/books/${bookId}/form-template/reset`, {}, { withCredentials: true });
      setTemplate(res.data.form_template);
      toast.success(t('formEditor.formRestored'));
    } catch (err) {
      toast.error(t('formEditor.restoreError'));
    }
  };

  const updateSection = (idx, section) => {
    const sections = [...template.sections];
    sections[idx] = section;
    setTemplate({ ...template, sections });
  };

  const addSection = () => {
    const newId = `section_${Date.now()}`;
    setTemplate({
      ...template,
      sections: [...template.sections, {
        id: newId, title: t('formEditor.newSection'), description: '', repeatable: false, max_repeat: 1,
        fields: [{ id: `field_${Date.now()}`, type: 'text', label: t('formEditor.newField'), placeholder: '', required: false }]
      }]
    });
  };

  const removeSection = (idx) => {
    const sections = [...template.sections];
    sections.splice(idx, 1);
    setTemplate({ ...template, sections });
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">{t('formEditor.loadingForm')}</div>;
  if (!template) return <div className="text-center py-8 text-red-500">{t('formEditor.loadError')}</div>;

  return (
    <div className="space-y-4" data-testid="form-template-editor">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Personaliza las preguntas que veran los colaboradores al aportar sus historias.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={resetTemplate}>
            <RotateCcw className="w-3 h-3" /> {t('formEditor.restore')}
          </Button>
          <Button size="sm" className="gap-1 text-xs" onClick={saveTemplate} disabled={saving}>
            <Save className="w-3 h-3" /> {saving ? t('notifications.saving') : t('common.save')}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {template.sections.map((section, idx) => (
          <SectionEditor key={section.id} section={section} onChange={(s) => updateSection(idx, s)} onDelete={() => removeSection(idx)} />
        ))}
      </div>

      <Button variant="outline" className="w-full gap-2" onClick={addSection}>
        <Plus className="w-4 h-4" /> {t('formEditor.addSection')}
      </Button>
    </div>
  );
};

export { SectionEditor, FieldEditor };

export default FormTemplateEditor;
